import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faTrash, faUpload, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { decryptData } from '../../../utils/storageCrypto';

export default function VendorAttachments({
  vendorId,
  isOpen,
  onClose,
  backendApiUrl,
  onAttachmentCountChange,
  onShowToast
}) {
  const [attachments, setAttachments] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState('');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [fileViewMode, setFileViewMode] = useState('image');
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const fileInputRef = useRef(null);

  const getHeaders = () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    return {
      ...(token ? { 'x-token': token } : {})
    };
  };

  // 1. GET: Fetch Vendor Attachments
  const fetchAttachments = async () => {
    if (!vendorId) return;
    try {
      const res = await fetch(`${backendApiUrl}vendor/get-vendor-attachments?vendorId=${vendorId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getHeaders()
        }
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        const list = Array.isArray(data.data) ? data.data : [];
        setAttachments(list);
        if (onAttachmentCountChange) onAttachmentCountChange(list.length);
      }
    } catch (err) {
      console.error('Error fetching vendor attachments:', err);
    }
  };

  useEffect(() => {
    if (vendorId) {
      fetchAttachments();
    }
  }, [vendorId]);

  // 2. PUT: Upload Attachment immediately on selection
  const handleFileUpload = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('vendorId', vendorId);

      try {
        const res = await fetch(`${backendApiUrl}vendor/add-vendor-attachments`, {
          method: 'PUT',
          body: formData,
          headers: getHeaders()
        });
        const data = await res.json();

        if (res.ok) {
          onShowToast?.('File uploaded successfully', '200or201');
          setAttachments((prev) => {
            const next = [...prev, file.name];
            if (onAttachmentCountChange) onAttachmentCountChange(next.length);
            return next;
          });
        } else {
          onShowToast?.(data.message || 'Failed to upload file', `${res.status}`);
        }
      } catch (err) {
        console.error('Error uploading file:', err);
        onShowToast?.('Error uploading file', '500');
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = null;
      }
    }
  };

  const handleViewAttachment = (attachment) => {
    setSelectedAttachment(attachment);
    setIsImageModalOpen(true);

    const ext = attachment.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      setFileViewMode('image');
    } else if (['pdf'].includes(ext)) {
      setFileViewMode('pdf');
    } else if (['doc', 'docx', 'xls', 'xlsx', 'csv'].includes(ext)) {
      setFileViewMode('office');
    } else {
      setFileViewMode('unsupported');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Attachments Dropdown Menu */}
      <div className="absolute right-5 z-20 top-28 bg-white shadow-xl border border-gray-200 rounded-[0.5rem] w-80">
        <div className="bg-gray-100 py-2 px-4 border-b flex justify-between items-center rounded-t-[0.5rem]">
          <h2 className="text-sm font-semibold text-gray-800">Attachments</h2>
          <button
            onClick={onClose}
            className="text-[#f7525a] hover:text-[#d63a42] p-1"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faX} className="w-3 h-3" />
          </button>
        </div>

        {/* Attachment List */}
        <div className="p-4 border-b max-h-52 overflow-y-auto">
          {attachments.length > 0 ? (
            attachments.map((attachment, index) => (
              <div key={index} className="flex items-center justify-between mb-2 last:mb-0">
                <span
                  className="text-xs text-blue-600 hover:underline cursor-pointer truncate max-w-[200px]"
                  onClick={() => handleViewAttachment(attachment)}
                  title={attachment}
                >
                  {attachment}
                </span>
              </div>
            ))
          ) : (
            <p className="text-xs text-gray-500 text-center">No files attached</p>
          )}
        </div>

        {/* Upload Button Box */}
        <div className="p-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 transition-colors">
            <div className="flex items-center justify-center gap-2">
              <FontAwesomeIcon
                icon={faUpload}
                className="text-blue-500 cursor-pointer text-sm"
                onClick={() => fileInputRef.current?.click()}
              />
              <p
                className="text-xs text-gray-700 cursor-pointer font-medium"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload your files
              </p>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="text-white rounded-full bg-gray-500 w-3 h-3 p-0.5 text-[10px] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUploadOptions(!showUploadOptions);
                }}
              />
            </div>
          </div>
          <p className="text-[10px] text-gray-400 text-center mt-1">
            Max 10 files, 10MB each (PDF, Images, Docs)
          </p>
        </div>

        {/* Upload Options Menu */}
        {showUploadOptions && (
          <div className="absolute right-4 bottom-2 bg-white border border-gray-200 rounded-md shadow-lg z-30 w-44">
            <button
              onClick={() => {
                setShowUploadOptions(false);
                fileInputRef.current?.click();
              }}
              className="w-full text-left text-xs text-gray-700 p-2 hover:bg-blue-500 hover:text-white"
            >
              Attach from computer
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileUpload}
          accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.csv,.doc,.docx"
        />
      </div>

      {/* Document / Image Preview Modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex justify-center items-center z-[9999] p-4">
          <div className="bg-white p-4 rounded-xl relative max-w-4xl max-h-[90vh] shadow-2xl">
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-600 transition-colors p-1"
              aria-label="Close preview"
            >
              <FontAwesomeIcon icon={faX} className="w-4 h-4" />
            </button>

            <div className="mt-4">
              {fileViewMode === 'image' && (
                <img
                  src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${selectedAttachment}`}
                  alt="Attachment Preview"
                  className="max-w-full max-h-[75vh] rounded object-contain mx-auto"
                />
              )}
              {fileViewMode === 'pdf' && (
                <div className="w-[800px] h-[600px] max-w-full">
                  <iframe
                    src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${selectedAttachment}`}
                    title="PDF Viewer"
                    width="100%"
                    height="100%"
                    className="border-none rounded"
                  />
                </div>
              )}
              {fileViewMode === 'office' && (
                <div className="w-[800px] h-[600px] max-w-full">
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                      process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + selectedAttachment
                    )}`}
                    title="Office Document Viewer"
                    width="100%"
                    height="100%"
                    className="border-none rounded"
                  />
                </div>
              )}
              {fileViewMode === 'unsupported' && (
                <div className="p-8 text-center text-sm text-gray-600">
                  This file type cannot be previewed directly. Please download it to view.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}